import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AccountConsent } from '@karehero/models';
import SettingsPage from '@karehero/llama/pwa/pages/SettingsPage/SettingsPage';
import { getProfilePictureUrl } from '@karehero/llama/pwa/utils/getProfilePictureUrl';
import type { Tab } from '@karehero/llama/components/atoms/Tabs/Tabs';
import { useGoBack } from 'hooks/useGoBack';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useSelector } from 'store';
import { getAccount } from 'store/slices/iamSlice';
import { useLazyVersionQuery } from 'store/api/iam';
import { useUpdateAccountConsentMutation } from 'store/api/account';
import { ROUTES } from 'router';
import { DevStateInspectorTab } from 'views/DevStateInspectorTab';

const AI_CONSENT_FORM_ID = 'ai-data-processing-v1';

const IS_NON_PROD =
  import.meta.env.REACT_APP_BUILD_ENVIRONMENT !== 'production';

const DEV_TABS: Tab[] = IS_NON_PROD
  ? [
      {
        id: 'dev-state',
        label: 'State',
        content: <DevStateInspectorTab />,
      },
    ]
  : [];

export const Settings = () => {
  const [apiVersion, setApiVersion] = useState<string>('unknown');

  const account = useSelector(getAccount);
  const navigate = useNavigate();
  const goBack = useGoBack();
  const [getApiVersion] = useLazyVersionQuery();
  const [updateConsent] = useUpdateAccountConsentMutation();
  const { flags } = useFeatureFlags();

  const avatarUrl = useMemo(() => getProfilePictureUrl(account), [account]);

  const isAiConsentEnabled = useMemo(
    () =>
      account?.accountConsents?.some(
        (c: AccountConsent) =>
          c.consentFormId === AI_CONSENT_FORM_ID && c.acceptedAt,
      ) ?? false,
    [account],
  );

  const handleAiConsentChange = useCallback(
    (enabled: boolean) => {
      updateConsent({ consents: { [AI_CONSENT_FORM_ID]: enabled } });
    },
    [updateConsent],
  );

  useEffect(() => {
    getApiVersion().then((res) => {
      if ('error' in res) {
        console.error(res.error);
        return;
      }
      setApiVersion(res.data?.message || 'unknown');
    });
  }, [getApiVersion]);

  return (
    <SettingsPage
      account={account}
      appVersion={import.meta.env.REACT_APP_BUILD_VERSION || 'local'}
      apiVersion={apiVersion}
      avatarUrl={avatarUrl}
      supportEmail={import.meta.env.REACT_APP_SUPPORT_EMAIL}
      onBack={goBack}
      onEditAvatar={() => navigate(ROUTES.AVATAR_EDITOR)}
      showEditAvatar={!!flags.isAvatars}
      showAiConsent={!!flags.aiDataProcessingConsent}
      isAiConsentEnabled={isAiConsentEnabled}
      onAiConsentChange={handleAiConsentChange}
      extraTabs={DEV_TABS}
    />
  );
};

export default Settings;
